/**
 * Put text on the clipboard; false when the browser refused.
 *
 * `navigator.clipboard` only exists in a secure context, and the app is
 * reached over plain HTTP on the tailnet. There the old route is the only
 * one: select the text in a field and ask for a copy — which Safari only
 * grants inside the tap itself, so nothing may be awaited before it.
 */
export async function copyText(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return copyBySelection(text);
    }
  }
  return copyBySelection(text);
}

function copyBySelection(text: string): boolean {
  const field = document.createElement("textarea");
  field.value = text;
  // Read-only keeps the iPhone keyboard down; 16px keeps Safari from zooming in.
  field.readOnly = true;
  field.style.cssText = "position:fixed;top:0;left:0;opacity:0;font-size:16px";
  document.body.append(field);
  field.focus({ preventScroll: true });
  field.select();
  field.setSelectionRange(0, text.length);
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.remove();
  }
}
